import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Alert,
  AlertTitle,
  Pagination,
  Chip
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Quiz as QuizIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MCQ } from '../../services/contentService';

interface McqsProps {
  mcqs: MCQ[];
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

const Mcqs: React.FC<McqsProps> = ({
  mcqs,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  
  // State for selected answers
  const [selectedAnswers, setSelectedAnswers] = useState<(number | null)[]>(mcqs.map(() => null));
  
  // State for showing explanations
  const [showExplanations, setShowExplanations] = useState<boolean[]>(mcqs.map(() => false));
  
  // State for quiz mode
  const [quizMode, setQuizMode] = useState<boolean>(true);
  
  // State for current page
  const [currentPage, setCurrentPage] = useState<number>(1);
  const questionsPerPage = 5;
  
  // State for quiz results
  const [quizSubmitted, setQuizSubmitted] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle copy
  const handleCopy = () => {
    const content = mcqs.map((mcq, index) => {
      const options = mcq.options.map((option, optIndex) => 
        `${String.fromCharCode(65 + optIndex)}. ${option}`
      ).join('\n');
      
      return `Question ${index + 1}: ${mcq.question}\n\n${options}\n\nCorrect Answer: ${String.fromCharCode(65 + mcq.correctAnswer)}\n\nExplanation: ${mcq.explanation}\n\n`;
    }).join('');
    
    navigator.clipboard.writeText(content);
    onCopy();
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMoreMenuClose();
  };
  
  // Handle download
  const handleDownload = () => {
    // Create content string
    const content = mcqs.map((mcq, index) => {
      const options = mcq.options.map((option, optIndex) => 
        `${String.fromCharCode(65 + optIndex)}. ${option}`
      ).join('\n');
      
      return `# Question ${index + 1}: ${mcq.question}\n\n${options}\n\n**Correct Answer:** ${String.fromCharCode(65 + mcq.correctAnswer)}\n\n**Explanation:** ${mcq.explanation}\n\n`;
    }).join('');
    
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and click it
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_mcqs.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onDownload();
    handleMoreMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    // Create content HTML
    const contentHtml = mcqs.map((mcq, index) => {
      const options = mcq.options.map((option, optIndex) => 
        `<div class="option ${mcq.correctAnswer === optIndex ? 'correct' : ''}">
          <span class="option-letter">${String.fromCharCode(65 + optIndex)}.</span> ${option}
        </div>`
      ).join('');
      
      return `<div class="mcq">
        <h2>Question ${index + 1}: ${mcq.question}</h2>
        <div class="options">${options}</div>
        <div class="explanation">
          <h3>Explanation:</h3>
          <p>${mcq.explanation}</p>
        </div>
      </div>`;
    }).join('');
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${videoTitle} - MCQs</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 20px;
                color: #333;
              }
              h1 {
                font-size: 24px;
                margin-bottom: 20px;
              }
              .time-saved {
                color: #06d6a0;
                font-weight: bold;
                margin-bottom: 20px;
              }
              .mcq {
                margin-bottom: 30px;
                page-break-inside: avoid;
              }
              h2 {
                font-size: 18px;
                margin-bottom: 15px;
              }
              .options {
                margin-bottom: 15px;
              }
              .option {
                margin-bottom: 8px;
                padding: 5px 10px;
              }
              .option.correct {
                background-color: #e8f5e9;
                border-left: 3px solid #4caf50;
              }
              .option-letter {
                font-weight: bold;
                margin-right: 5px;
              }
              .explanation {
                background-color: #f5f5f5;
                padding: 10px;
                border-radius: 4px;
              }
              h3 {
                font-size: 16px;
                margin-top: 0;
                margin-bottom: 10px;
              }
              @media print {
                body {
                  font-size: 12pt;
                }
                h2 {
                  font-size: 14pt;
                }
                .mcq {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <h1>${videoTitle} - Multiple Choice Questions</h1>
            <div class="time-saved">Saved ${timeSaved} minutes</div>
            ${contentHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    
    onPrint();
    handleMoreMenuClose();
  };
  
  // Handle answer selection
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (quizMode && !quizSubmitted) {
      const newSelectedAnswers = [...selectedAnswers];
      newSelectedAnswers[questionIndex] = optionIndex;
      setSelectedAnswers(newSelectedAnswers);
    }
  };
  
  // Handle explanation toggle
  const handleExplanationToggle = (questionIndex: number) => {
    const newShowExplanations = [...showExplanations];
    newShowExplanations[questionIndex] = !newShowExplanations[questionIndex];
    setShowExplanations(newShowExplanations);
  };
  
  // Handle quiz mode toggle
  const handleQuizModeToggle = () => {
    setQuizMode(!quizMode);
    setQuizSubmitted(false);
    setSelectedAnswers(mcqs.map(() => null));
    setShowExplanations(mcqs.map(() => !quizMode));
  };
  
  // Handle quiz submit
  const handleQuizSubmit = () => {
    // Calculate score
    const correctAnswers = selectedAnswers.filter(
      (answer, index) => answer === mcqs[index].correctAnswer
    ).length;
    
    setScore(correctAnswers);
    setQuizSubmitted(true);
    setShowExplanations(mcqs.map(() => true));
  };
  
  // Handle quiz reset
  const handleQuizReset = () => {
    setSelectedAnswers(mcqs.map(() => null));
    setShowExplanations(mcqs.map(() => false));
    setQuizSubmitted(false);
  };
  
  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };
  
  // Get current questions
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = mcqs.slice(indexOfFirstQuestion, indexOfLastQuestion);
  
  // Calculate total pages
  const totalPages = Math.ceil(mcqs.length / questionsPerPage);
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuizIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Multiple Choice Questions
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Test your understanding with these questions based on the video content.
        </Typography>
        
        <Button
          variant="outlined"
          size="small"
          onClick={handleQuizModeToggle}
          startIcon={quizMode ? <CheckIcon /> : <QuizIcon />}
          sx={{ borderRadius: 4 }}
        >
          {quizMode ? 'Study Mode' : 'Quiz Mode'}
        </Button>
      </Box>
      
      {quizMode && quizSubmitted && (
        <Alert 
          severity={score / mcqs.length >= 0.7 ? "success" : "warning"}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleQuizReset}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>
            {score / mcqs.length >= 0.7 ? "Great job!" : "Keep practicing!"}
          </AlertTitle>
          You scored {score} out of {mcqs.length} ({Math.round((score / mcqs.length) * 100)}%)
        </Alert>
      )}
      
      <Divider sx={{ mb: 3 }} />
      
      {currentQuestions.map((mcq, questionIndex) => {
        const actualIndex = indexOfFirstQuestion + questionIndex;
        const isCorrect = selectedAnswers[actualIndex] === mcq.correctAnswer;
        const isWrong = selectedAnswers[actualIndex] !== null && !isCorrect;
        
        return (
          <Card 
            key={actualIndex} 
            elevation={0}
            sx={{ 
              mb: 3, 
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'transparent'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography 
                  variant="subtitle1" 
                  component="div" 
                  sx={{ 
                    fontWeight: 600,
                    pr: 2
                  }}
                >
                  <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                    {actualIndex + 1}.
                  </Box>
                  {mcq.question}
                </Typography>
                
                {quizSubmitted && (
                  <Chip 
                    icon={isCorrect ? <CheckIcon /> : <CloseIcon />}
                    label={isCorrect ? "Correct" : "Incorrect"}
                    color={isCorrect ? "success" : "error"}
                    size="small"
                    sx={{ borderRadius: 4 }}
                  />
                )}
              </Box>
              
              <RadioGroup 
                value={selectedAnswers[actualIndex]}
                onChange={(e) => handleAnswerSelect(actualIndex, parseInt(e.target.value))}
              >
                {mcq.options.map((option, optionIndex) => (
                  <FormControlLabel
                    key={optionIndex}
                    value={optionIndex}
                    control={<Radio />}
                    label={
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: quizSubmitted && mcq.correctAnswer === optionIndex 
                            ? 'success.main' 
                            : (quizSubmitted && selectedAnswers[actualIndex] === optionIndex && isWrong)
                              ? 'error.main'
                              : 'text.primary'
                        }}
                      >
                        {option}
                      </Typography>
                    }
                    sx={{ 
                      mb: 1,
                      ml: 0,
                      pl: 1,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      },
                      ...(quizSubmitted && mcq.correctAnswer === optionIndex && {
                        bgcolor: 'rgba(6, 214, 160, 0.1)',
                        border: '1px solid rgba(6, 214, 160, 0.2)',
                      }),
                      ...(quizSubmitted && selectedAnswers[actualIndex] === optionIndex && isWrong && {
                        bgcolor: 'rgba(239, 71, 111, 0.1)',
                        border: '1px solid rgba(239, 71, 111, 0.2)',
                      })
                    }}
                    disabled={quizMode && quizSubmitted}
                  />
                ))}
              </RadioGroup>
              
              {(!quizMode || quizSubmitted || showExplanations[actualIndex]) && (
                <Box 
                  sx={{ 
                    mt: 2,
                    p: 2,
                    bgcolor: 'rgba(0, 180, 216, 0.05)',
                    border: '1px solid rgba(0, 180, 216, 0.1)',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Explanation:
                  </Typography>
                  <Box 
                    sx={{ 
                      typography: 'body2',
                      '& p': {
                        mb: 1,
                        '&:last-child': {
                          mb: 0
                        }
                      },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {mcq.explanation}
                    </ReactMarkdown>
                  </Box>
                </Box>
              )}
              
              {quizMode && !quizSubmitted && !showExplanations[actualIndex] && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleExplanationToggle(actualIndex)}
                  sx={{ mt: 1 }}
                >
                  Show Explanation
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
      
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={handlePageChange} 
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
      
      {quizMode && !quizSubmitted && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleQuizSubmit}
            disabled={selectedAnswers.some(answer => answer === null)}
            sx={{ 
              borderRadius: 4,
              px: 4
            }}
          >
            Submit Answers
          </Button>
        </Box>
      )}
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default Mcqs;
